/**
 * Checkout validation.
 *
 * Mirrors the checks in `public.place_order` so the customer sees a problem
 * next to the field rather than as a server error after tapping Pay. The
 * database re-validates everything; these schemas are for feedback only.
 */

import { z } from 'zod';

/**
 * Normalises a phone number the same way `private.normalize_phone` does:
 * keep a leading `+`, strip every other non-digit.
 *
 * @param input Raw text typed by the customer.
 * @returns The normalised number.
 */
export function normalizePhone(input: string): string {
  const trimmed = input.trim();
  const digits = trimmed.replace(/\D/g, '');
  return trimmed.startsWith('+') ? `+${digits}` : digits;
}

/** Mobile is mandatory: the restaurant must be able to reach the customer. */
export const phoneSchema = z
  .string()
  .trim()
  .min(1, 'A mobile number is required')
  .transform(normalizePhone)
  .refine((value) => value.replace(/\D/g, '').length >= 6, {
    message: 'That number looks too short',
  })
  .refine((value) => value.replace(/\D/g, '').length <= 15, {
    message: 'That number looks too long',
  })
    .refine((value) => !/^(\d)\1+$/.test(value.replace(/\D/g, '')), {
      message: 'That number is not a real one',
    });

/**
 * How long a local number is, per country, and its dialling code.
 *
 * Only used to say something helpful beside the field — never to refuse. A
 * number's country is not knowable: a traveller at a Singapore restaurant holds
 * an Australian number, and someone on a VPN looks foreign while standing in the
 * queue. A hint that is occasionally wrong costs nothing; a rule that is
 * occasionally wrong costs an order.
 *
 * Countries outside this list get no hint at all, which is the safe direction.
 *
 * `min` and `max` are of the number without its trunk, so Australia is nine
 * whether it is dialled 0412 345 678 or 412 345 678 — a leading zero is not a
 * tenth digit, and treating it as one let 4123456789 through to be stored
 * unringable.
 *
 * `example` is written the way the country writes a number at home, trunk
 * prefix and all, because that is what the placeholder shows and what the
 * customer types. The trunk is dropped when the number is written with its
 * code — 0422 169 412 at home is +61 422 169 412 abroad.
 */
export const PHONE_PLANS: Record<string, { min: number; max: number; dial: string; example: string }> = {
  SG: { min: 8, max: 8, dial: '+65', example: '9123 4567' },
  AU: { min: 9, max: 9, dial: '+61', example: '0412 345 678' },
  MY: { min: 9, max: 10, dial: '+60', example: '12 345 6789' },
  TW: { min: 9, max: 9, dial: '+886', example: '912 345 678' },
  HK: { min: 8, max: 8, dial: '+852', example: '9123 4567' },
  CN: { min: 11, max: 11, dial: '+86', example: '131 2345 6789' },
  JP: { min: 10, max: 11, dial: '+81', example: '90 1234 5678' },
  TH: { min: 9, max: 9, dial: '+66', example: '81 234 5678' },
  ID: { min: 9, max: 12, dial: '+62', example: '812 3456 7890' },
  PH: { min: 10, max: 10, dial: '+63', example: '917 123 4567' },
  IN: { min: 10, max: 10, dial: '+91', example: '98765 43210' },
  GB: { min: 10, max: 11, dial: '+44', example: '7400 123456' },
  US: { min: 10, max: 10, dial: '+1', example: '202 555 0143' },
};

/**
 * What is worth saying about a number beside the field.
 *
 * @param phone The number as typed.
 * @param country The restaurant's country, which is where the order is collected.
 * @returns A reason and its figures, or null when there is nothing useful to say.
 */
/**
 * An example number for the restaurant's country, for the field's placeholder.
 *
 * A hardcoded Singapore example reads wrong at an Australian restaurant, and the
 * placeholder is the first thing a customer sees in the field.
 *
 * @param country The restaurant's country, or null.
 * @returns A dialling code and an example, or null when the country is unknown.
 */
export function phonePlaceholder(country: string | null): string | null {
  const plan = country ? PHONE_PLANS[country] : undefined;
  return plan ? `${plan.dial} ${plan.example}` : null;
}

/**
 * A number written the way its country writes it, for the field to show back.
 *
 * The example in each plan is already written the local way — 9123 4567 for
 * Singapore, 412 345 678 for Australia — so the grouping comes from it rather
 * than from a second table that could drift out of step with the placeholder.
 *
 * Only formats when the length is the country's own: a number of the wrong
 * length is being reported as such by phoneHintFor, and dressing it up would
 * only make the wrong number look considered.
 *
 * @param phone The number as typed.
 * @param country The restaurant's country, or null.
 * @returns The dialling code and grouped digits, or null when it cannot say.
 */
/**
 * The number with its country's dialling code, for a number typed without one.
 *
 * Only when the digits are a plausible local number for that country: a number
 * of the wrong length is being reported as wrong beside the field, and silently
 * completing it would store something the customer never saw.
 *
 * @param phone The number as typed or remembered.
 * @param country The restaurant's country, or null.
 * @returns The number with its code, or unchanged when it cannot say.
 */
export function withDialCode(phone: string, country: string | null): string {
  const plan = country ? PHONE_PLANS[country] : undefined;
  if (!plan || phone.trim().startsWith('+')) return phone;
  const digits = phone.replace(/\D/g, '');
  if (digits.length < plan.min || digits.length > plan.max) return phone;
  return `${plan.dial}${digits.replace(/^0/, '')}`;
}

export function formatPhoneFor(phone: string, country: string | null): string | null {
  const plan = country ? PHONE_PLANS[country] : undefined;
  if (!plan) return null;
  const typed = phone.replace(/\D/g, '');
  if (typed.length === 0) return null;
  // Someone who types their own country code has typed the same number: strip
  // it before measuring, or +61422169412 looks eleven digits long against a
  // nine-digit example and is handed back ungrouped.
  const code = plan.dial.replace('+', '');
  const withoutCode = typed.startsWith(code) ? typed.slice(code.length) : typed;
  // The trunk 0 is dropped when a number is written with its country code, so
  // both sides lose it before they are compared.
  const digits = withoutCode.replace(/^0/, '');
  const expected = plan.example.replace(/\D/g, '').replace(/^0/, '');
  if (digits.length !== expected.length) {
    return `${plan.dial} ${digits}`;
  }
  // Group from the right, so the tail the country writes as one block stays one.
  const groups = plan.example.split(' ');
  const out: string[] = [];
  let rest = digits;
  for (let i = groups.length - 1; i >= 0; i -= 1) {
    const take = groups[i]?.length ?? 0;
    out.unshift(rest.slice(Math.max(0, rest.length - take)));
    rest = rest.slice(0, Math.max(0, rest.length - take));
  }
  if (rest) out.unshift(rest);
  return `${plan.dial} ${out.join(' ')}`;
}

export function phoneHintFor(
  phone: string,
  country: string | null,
): { kind: 'short' | 'long' | 'foreign'; min: number; max: number; dial: string } | null {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 0 || !country) return null;

  if (phone.trim().startsWith('+')) {
    const own = (PHONE_PLANS[country]?.dial ?? '').replace('+', '');
    if (!own || !digits.startsWith(own)) {
      // Somebody else's code: right shape, and worth naming whose it is.
      const plan = Object.values(PHONE_PLANS).find((p) => digits.startsWith(p.dial.replace('+', '')));
      return plan ? { kind: 'foreign', min: plan.min, max: plan.max, dial: plan.dial } : null;
    }
    // Its own code, so the local part is what to measure.
    const home = PHONE_PLANS[country];
    if (!home) return null;
    // Measured as typed, trunk and all: the bounds are of the local number,
    // which is 9 or 10 digits in Australia depending on whether it is dialled
    // at home.
    const local = digits.slice(own.length).replace(/^0/, '');
    if (local.length < home.min) return { kind: 'short', ...home };
    if (local.length > home.max) return { kind: 'long', ...home };
    return null;
  }

  const plan = PHONE_PLANS[country];
  if (!plan) return null;
  // The bounds are of the number without its trunk, so a leading zero comes off
  // here as well. Nine digits in Australia is nine whether it was dialled
  // 0412 345 678 or 412 345 678.
  const bare = digits.replace(/^0/, '');
  if (bare.length < plan.min) return { kind: 'short', ...plan };
  if (bare.length > plan.max) return { kind: 'long', ...plan };
  return null;
}

/** Email is optional, but must be plausible when supplied. */
export const optionalEmailSchema = z
  .string()
  .trim()
  .max(254, 'That email address is too long')
  .refine((value) => value === '' || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value), {
    message: 'That email address does not look right',
  })
  .transform((value) => (value === '' ? null : value));

export const customerNameSchema = z
  .string()
  .trim()
  .min(1, 'Please tell us your name')
  .max(80, 'That name is too long');

export const noteSchema = z
  .string()
  .trim()
  .max(500, 'Please keep notes under 500 characters')
  .transform((value) => (value === '' ? null : value));

/** The full checkout form. */
export const checkoutSchema = z.object({
  name: customerNameSchema,
  phone: phoneSchema,
  email: optionalEmailSchema,
  note: noteSchema,
});

export type CheckoutInput = z.input<typeof checkoutSchema>;
export type CheckoutValues = z.output<typeof checkoutSchema>;

/** Merchant signup / login credentials. */
export const credentialsSchema = z.object({
  email: z.string().trim().email('Enter a valid email address'),
  password: z.string().min(8, 'Use at least 8 characters'),
});

export type Credentials = z.infer<typeof credentialsSchema>;

/**
 * The message shown when a restaurant has no collection address.
 *
 * Exported because two forms enforce the rule — setup and settings — and the
 * database enforces it a third time, so the sentence should be written once.
 */
export const ADDRESS_REQUIRED_MESSAGE = 'Customers need an address to collect from';

/**
 * Raised when the web address a merchant picked is already in use.
 *
 * Shared between the database and the form: only the database knows whether an
 * address is taken, so the form matches this message to put the error under the
 * address field rather than in a banner at the top.
 */
export const SLUG_TAKEN_MESSAGE = 'That web address is already taken. Try another.';

/** Restaurant creation, matching the constraints on `public.restaurants`. */
export const restaurantSetupSchema = z.object({
  name: z.string().trim().min(1, 'Your restaurant needs a name').max(120),
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'A web address is required')
    .max(64)
    .regex(
      /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/,
      'Use lowercase letters, numbers, and hyphens; it must start and end with a letter or number',
    ),
  timezone: z.string().trim().min(1),
  currency: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'Use a three-letter currency code such as SGD'),
  phone: z.string().trim().max(32).optional().default(''),
  // Required, unlike phone and description: the address is the only thing on an
  // order that tells the customer where to walk to, so a restaurant without one
  // takes an order nobody can collect.
  address: z.string().trim().min(1, ADDRESS_REQUIRED_MESSAGE).max(300),
  description: z.string().trim().max(600).optional().default(''),
  /** ISO 3166-1 alpha-2, as the onboarding's country select chose it. */
  country: z.string().trim().length(2).optional(),
});

export type RestaurantSetupInput = z.input<typeof restaurantSetupSchema>;
export type RestaurantSetupValues = z.output<typeof restaurantSetupSchema>;

/**
 * Turns a restaurant name into a candidate web address.
 *
 * Apostrophes are removed rather than treated as separators, so "Joseph's" reads
 * as `josephs` instead of `joseph-s`.
 *
 * @param name The restaurant's display name.
 * @returns A slug safe to use in a URL path.
 */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['\u2018\u2019\u02bc]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/-+$/g, '');
}

/**
 * Flattens a Zod error into a `field -> message` map for form rendering.
 *
 * @param error A ZodError, or anything else.
 * @returns One message per field; `_form` for errors with no field path.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  const result: Record<string, string> = {};

  if (error && typeof error === 'object' && 'issues' in error) {
    const issues = (error as { issues: Array<{ path: (string | number)[]; message: string }> }).issues;
    for (const issue of issues) {
      const key = issue.path.length > 0 ? issue.path.join('.') : '_form';
      result[key] ??= issue.message;
    }
    return result;
  }

  if (error instanceof Error) result._form = error.message;
  return result;
}
